import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X, Loader2, CheckSquare, AlertTriangle } from 'lucide-react';
import { tasksApi } from '@/lib/api';
import { Button } from '@/components/ui/Button';

type ListVisibility = 'shared' | 'agency_only' | 'client_only';

interface CreateTaskListModalProps {
  isOpen: boolean;
  projectId: string;
  onClose: () => void;
  onSuccess?: (taskList: any) => void;
  taskList?: { id: string; name: string; description?: string; visibility?: string }; // For editing
  isAgencyMember?: boolean;
  isSystemAdmin?: boolean;
}

const VISIBILITY_OPTIONS_AGENCY: { value: ListVisibility; label: string; description: string }[] = [
  { value: 'shared', label: 'Everyone', description: 'Visible to all project members' },
  { value: 'agency_only', label: 'Solid Digital', description: 'Only visible to your agency team' },
  { value: 'client_only', label: 'Client', description: 'Only visible to the client team' },
];

const VISIBILITY_OPTIONS_CLIENT: { value: ListVisibility; label: string; description: string }[] = [
  { value: 'shared', label: 'Everyone', description: 'Visible to all project members' },
  { value: 'client_only', label: 'My Team', description: 'Only visible to your team' },
];

export function CreateTaskListModal({
  isOpen,
  projectId,
  onClose,
  onSuccess,
  taskList,
  isAgencyMember = false,
  isSystemAdmin = false,
}: CreateTaskListModalProps) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState<ListVisibility>('shared');
  const [showConflictWarning, setShowConflictWarning] = useState(false);

  const isEditing = !!taskList;
  const visibilityOptions = isAgencyMember ? VISIBILITY_OPTIONS_AGENCY : VISIBILITY_OPTIONS_CLIENT;

  // Check for conflicting assignments when changing visibility on an existing list
  const conflictsQuery = useQuery({
    queryKey: ['taskListConflicts', projectId, taskList?.id, visibility],
    queryFn: () => tasksApi.checkVisibilityConflicts(projectId, taskList!.id, visibility),
    enabled: isEditing && !!taskList?.id && visibility !== (taskList?.visibility ?? 'shared'),
  });

  const conflicts = conflictsQuery.data ?? [];

  // Show conflict warning when visibility changes and there are conflicts
  useEffect(() => {
    if (isEditing && conflicts.length > 0 && visibility !== (taskList?.visibility ?? 'shared')) {
      setShowConflictWarning(true);
    } else {
      setShowConflictWarning(false);
    }
  }, [conflicts, visibility, isEditing, taskList?.visibility]);

  // Populate form when editing
  useEffect(() => {
    if (taskList) {
      setName(taskList.name);
      setDescription(taskList.description || '');
      setVisibility((taskList.visibility as ListVisibility) ?? 'shared');
    } else {
      setName('');
      setDescription('');
      setVisibility('shared');
    }
  }, [taskList, isOpen]);

  const createMutation = useMutation({
    mutationFn: (data: { name: string; description?: string; visibility: string }) =>
      tasksApi.createTaskList(projectId, data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      queryClient.invalidateQueries({ queryKey: ['taskLists', projectId] });
      resetAndClose();
      onSuccess?.(result);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: { name: string; description?: string; visibility?: string }) =>
      tasksApi.updateTaskList(projectId, taskList!.id, data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      queryClient.invalidateQueries({ queryKey: ['taskLists', projectId] });
      queryClient.invalidateQueries({ queryKey: ['taskList', taskList!.id] });
      resetAndClose();
      onSuccess?.(result);
    },
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  const resetAndClose = () => {
    setName('');
    setDescription('');
    setVisibility('shared');
    setShowConflictWarning(false);
    onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    const data = {
      name: name.trim(),
      description: description.trim() || undefined,
      visibility,
    };

    if (isEditing) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  if (!isOpen) return null;

  // Determine if the user can change visibility on this list
  // Agency can't edit client_only lists (unless system admin); clients can't edit agency_only
  const canChangeVisibility = isEditing
    ? (taskList?.visibility === 'client_only' ? (!isAgencyMember || isSystemAdmin) : (taskList?.visibility === 'agency_only' ? isAgencyMember : true))
    : true;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-secondary/60 backdrop-blur-sm animate-fade-in"
        onClick={resetAndClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-elevated animate-scale-in overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-grey-100">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-success/10">
              <CheckSquare className="w-5 h-5 text-success" />
            </div>
            <h2 className="text-heading-4 text-secondary">
              {isEditing ? 'Edit Task List' : 'New Task List'}
            </h2>
          </div>
          <button
            onClick={resetAndClose}
            className="p-2 hover:bg-grey-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-grey-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="px-6 py-6 space-y-5">
            {/* List Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-secondary mb-2">
                List Name <span className="text-error">*</span>
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Design Phase, Launch Checklist"
                className="input"
                required
                autoFocus
              />
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-secondary mb-2">
                Description <span className="text-grey-400">(optional)</span>
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What tasks will be tracked in this list?"
                rows={3}
                className="input resize-none"
              />
            </div>

            {/* Visibility Selector — "Who is this for?" */}
            <div>
              <label className="block text-sm font-medium text-secondary mb-2">
                Who is this for?
              </label>
              <div className="flex gap-2">
                {visibilityOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    disabled={!canChangeVisibility}
                    onClick={() => setVisibility(option.value)}
                    className={`flex-1 px-3 py-2.5 rounded-lg border-2 text-sm font-medium transition-all ${
                      visibility === option.value
                        ? 'border-accent bg-accent/5 text-accent'
                        : 'border-grey-200 bg-white text-grey-600 hover:border-grey-300'
                    } ${!canChangeVisibility ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              <p className="text-xs text-grey-500 mt-1.5">
                {visibilityOptions.find((o) => o.value === visibility)?.description}
              </p>
            </div>

            {/* Conflict Warning */}
            {showConflictWarning && conflicts.length > 0 && (
              <div className="flex items-start gap-3 p-3 rounded-lg bg-warning/10 border border-warning/20">
                <AlertTriangle className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-secondary">
                    {conflicts.length} task{conflicts.length > 1 ? 's have' : ' has'} assignees who won't be able to see this list
                  </p>
                  <ul className="text-xs text-grey-600 mt-1 space-y-0.5">
                    {conflicts.slice(0, 5).map((task: any) => (
                      <li key={task.id}>
                        "{task.title}" — assigned to {task.assignee?.name ?? 'unknown'}
                      </li>
                    ))}
                    {conflicts.length > 5 && (
                      <li>...and {conflicts.length - 5} more</li>
                    )}
                  </ul>
                  <p className="text-xs text-grey-500 mt-1.5">
                    Consider reassigning these tasks before changing visibility.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 bg-grey-50 border-t border-grey-100">
            <Button type="button" variant="ghost" onClick={resetAndClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!name.trim() || isPending}
            >
              {isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {isEditing ? 'Saving...' : 'Creating...'}
                </>
              ) : (
                isEditing ? 'Save Changes' : 'Create List'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
